/**
 * M34 A7 · el bloque «Impuestos incluidos» de la tirilla (textos de Producto c16f6a5). Con red manda lo que congeló el
 * -mt en cada línea (A1/A3: `impuestoCodigo`, `impuestoTarifa`, `impuestoBase`, `impuestoValor`); recién creada la venta
 * pueden venir en null y, sin red, no vienen: entonces lo calcula la caja como el disparador del -mt
 * (`ImpuestoPorLineaTest`, mismos vectores en el spec). Al sincronizar no se reimprime.
 */

/** Una fila del catálogo (`GET /api/menu/impuestos`), lo que el papel necesita. */
export interface TarifaDelCatalogo {
    codigo: string;
    nombre: string;
    tributo: 'IVA' | 'INC' | 'EXCLUIDO' | 'INC_BOLSAS' | string;
    tipo: 'PORCENTAJE' | 'POR_UNIDAD';
    tarifa: number;
}

/** Lo que una línea aporta al bloque. `cobrado` = total de la línea menos su descuento. */
export interface LineaConImpuesto {
    impuestoCodigo?: string | null;
    impuestoBase?: number | null;
    impuestoValor?: number | null;
    cobrado: number;
    cantidad: number;
}

export interface FilaDeImpuesto {
    nombre: string;
    base: number;
    valor: number;
}

/** Redondeo a centavos «alejándose de cero», como PG; con valores positivos es el de siempre. */
const centavos = (x: number) => Math.sign(x) * Math.round(Math.abs(x) * 100) / 100;

/** Base e impuesto de una línea, como el disparador de A1. */
export function impuestoDeLinea(cobrado: number, cantidad: number, t: TarifaDelCatalogo): { base: number; valor: number } {
    if (t.tipo === 'POR_UNIDAD') {
        const valor = Math.min(cantidad * t.tarifa, cobrado);
        return { base: centavos(cobrado - valor), valor };
    }
    const base = centavos(cobrado / (1 + t.tarifa / 100));
    return { base, valor: centavos(cobrado - base) };
}

/**
 * Una fila por tarifa presente en la venta, en el orden en que aparece. Sin tarifa (null) o `NINGUNO` (no responsable,
 * o impuestos que no aplican) no sale. Un código que el catálogo no conoce se muestra con su código, y sin catálogo no
 * se puede calcular: si la línea no trae lo del servidor, se salta (no se inventa un impuesto).
 */
export function desglosePorTarifa(lineas: LineaConImpuesto[], catalogo: TarifaDelCatalogo[]): FilaDeImpuesto[] {
    const filas = new Map<string, FilaDeImpuesto>();
    for (const l of lineas) {
        const codigo = l.impuestoCodigo;
        if (!codigo || codigo === 'NINGUNO') continue;
        const t = catalogo.find((c) => c.codigo === codigo);
        const delServidor = typeof l.impuestoBase === 'number' && typeof l.impuestoValor === 'number';
        if (!delServidor && !t) continue;
        const { base, valor } = delServidor ? { base: l.impuestoBase!, valor: l.impuestoValor! } : impuestoDeLinea(l.cobrado, l.cantidad, t!);
        const fila = filas.get(codigo) ?? { nombre: t?.nombre ?? codigo, base: 0, valor: 0 };
        fila.base = centavos(fila.base + base);
        fila.valor = centavos(fila.valor + valor);
        filas.set(codigo, fila);
    }
    return [...filas.values()];
}

/**
 * El descuento de la venta repartido entre sus líneas, como el -mt (`DescuentoPorLinea.repartir`, algoritmo V11 de
 * pedidos): solo entre las elegibles, en proporción a su bruto y sobre lo menor entre el descuento y la suma de los
 * brutos elegibles. Cada una recibe su parte al centavo hacia abajo, y los centavos que faltan van de uno en uno a las de
 * mayor resto (si empatan, a la de menor posición). La suma sale exacta. Devuelve lo cobrado de cada línea.
 *
 * ponytail: la caja no sabe qué productos cubre un cupón (aplicar solo devuelve el monto), así que sin `elegibles` todas
 * lo son: igual al -mt con un cupón general. Con un cupón de ciertos productos y sin red, puede diferir; con red manda lo
 * congelado.
 */
export function cobradoPorLinea(totales: number[], descuento: number | null | undefined, elegibles?: boolean[]): number[] {
    const aCentavos = (x: number) => Math.round(x * 100);
    const brutos = totales.map(aCentavos);
    const elegible = (i: number) => elegibles?.[i] ?? true;
    const sumaElegibles = brutos.reduce((a, b, i) => a + (elegible(i) ? b : 0), 0);
    const repartir = Math.min(aCentavos(descuento && descuento > 0 ? descuento : 0), sumaElegibles);
    if (!repartir || sumaElegibles <= 0) return totales.slice();
    const partes = brutos.map((b, i) => (elegible(i) ? Math.floor((repartir * b) / sumaElegibles) : 0));
    const restos = brutos.map((b, i) => ({ i, resto: elegible(i) ? (repartir * b) % sumaElegibles : -1 }));
    let faltan = repartir - partes.reduce((a, b) => a + b, 0);
    for (const { i } of restos.filter((r) => r.resto >= 0).sort((a, b) => b.resto - a.resto || a.i - b.i)) {
        if (faltan-- <= 0) break;
        partes[i]++;
    }
    return brutos.map((b, i) => (b - partes[i]) / 100);
}

/**
 * M34 B2 · la propina sugerida va sobre el consumo SIN impuestos (C23): lo cobrado menos el impuesto de cada línea. Una
 * línea sin tarifa (o con un código que el catálogo no conoce) cuenta entera: no se descuenta un impuesto que no se sabe.
 */
export function baseSinImpuestos(lineas: LineaConImpuesto[], catalogo: TarifaDelCatalogo[]): number {
    return centavos(lineas.reduce((suma, l) => {
        const t = l.impuestoCodigo && l.impuestoCodigo !== 'NINGUNO' ? catalogo.find((c) => c.codigo === l.impuestoCodigo) : undefined;
        return suma + (t ? impuestoDeLinea(l.cobrado, l.cantidad, t).base : l.cobrado);
    }, 0));
}

/** La propina sugerida en pesos enteros: `pct` % de la base, redondeada al peso. */
export const propinaSugerida = (base: number, pct: number): number => Math.round((base * pct) / 100);

/**
 * M34 B1.7 (revisión de DrDev002): una propina de más del 20 % del consumo se pregunta antes de cobrar (Producto
 * 2e4ccf4: el doble del tope de la sugerida). El -mt no la topa; el aviso caza un cero de más.
 */
export const UMBRAL_PROPINA_ALTA = 0.2;
export const propinaAlta = (propina: number, consumo: number): boolean => propina > 0 && propina > consumo * UMBRAL_PROPINA_ALTA;

/** El aviso de Producto con los montos a la vista; null si no hace falta preguntar. */
export function avisoDePropinaAlta(propina: number, consumo: number, pesos: (v: number) => string): { title: string; text: string } | null {
    return propinaAlta(propina, consumo)
        ? { title: '¿La propina es correcta?', text: `La propina es ${pesos(propina)}, más del 20 % del consumo (${pesos(consumo)}). Total a pagar: ${pesos(consumo + propina)}.` }
        : null;
}
